import { useEffect, useMemo, useState } from "react";
import { FilePenLine, NotebookPen, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { createNote, deleteNote, fetchNotes, updateNote } from "@/lib/api";
import { pickText } from "@/lib/content";

const initialForm = { author_name: "", title: "", content: "" };

export const NotesPanel = ({ itemSlug, language }) => {
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");

  const locale = useMemo(() => (language === "it" ? "it-IT" : "en-US"), [language]);

  useEffect(() => {
    const loadNotes = async () => {
      if (!itemSlug) {
        setNotes([]);
        return;
      }

      try {
        setLoading(true);
        const data = await fetchNotes(itemSlug);
        setNotes(data);
      } catch (error) {
        console.error(error);
        toast.error(pickText(language, "Notes could not load.", "Le note non possono essere caricate."));
      } finally {
        setLoading(false);
      }
    };

    setForm(initialForm);
    setEditingId("");
    loadNotes();
  }, [itemSlug, language]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!itemSlug) return;

    try {
      setSaving(true);

      if (editingId) {
        const updated = await updateNote(editingId, { ...form, language });
        setNotes((current) => current.map((note) => (note.id === editingId ? updated : note)));
        toast.success(pickText(language, "Note updated.", "Nota aggiornata."));
      } else {
        const created = await createNote({ ...form, item_slug: itemSlug, language });
        setNotes((current) => [created, ...current]);
        toast.success(pickText(language, "Note added.", "Nota aggiunta."));
      }

      setForm(initialForm);
      setEditingId("");
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Note could not be saved.", "La nota non può essere salvata."));
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (note) => {
    setEditingId(note.id);
    setForm({ author_name: note.author_name, title: note.title, content: note.content });
  };

  const cancelEditing = () => {
    setEditingId("");
    setForm(initialForm);
  };

  const handleDelete = async (noteId) => {
    try {
      await deleteNote(noteId);
      setNotes((current) => current.filter((note) => note.id !== noteId));
      if (editingId === noteId) cancelEditing();
      toast.success(pickText(language, "Note removed.", "Nota rimossa."));
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Note could not be removed.", "La nota non può essere rimossa."));
    }
  };

  return (
    <div className="space-y-5" data-testid="notes-panel">
      <div className="flex items-center gap-3" data-testid="notes-panel-header">
        <span className="rounded-full border border-primary/30 bg-primary/10 p-2 text-primary">
          <NotebookPen className="h-4 w-4" />
        </span>
        <div>
          <h4 className="text-xl text-foreground" data-testid="notes-panel-title">
            {pickText(language, "Field Notes", "Note di Campo")}
          </h4>
          <p className="text-sm text-slate-400" data-testid="notes-panel-subtitle">
            {pickText(language, "Attach your own observations to this node.", "Collega le tue osservazioni a questo nodo.")}
          </p>
        </div>
      </div>

      <form className="space-y-3" data-testid="notes-form" onSubmit={handleSubmit}>
        <Input
          className="border-white/10 bg-black/20 text-white"
          data-testid="notes-author-input"
          name="author_name"
          onChange={handleChange}
          placeholder={pickText(language, "Archivist name", "Nome dell'archivista")}
          value={form.author_name}
        />
        <Input
          className="border-white/10 bg-black/20 text-white"
          data-testid="notes-title-input"
          name="title"
          onChange={handleChange}
          placeholder={pickText(language, "Observation title", "Titolo dell'osservazione")}
          required
          value={form.title}
        />
        <textarea
          className="min-h-28 w-full rounded-3xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white outline-none transition-colors duration-300 placeholder:text-slate-500 focus:border-primary/40"
          data-testid="notes-content-textarea"
          name="content"
          onChange={handleChange}
          placeholder={pickText(language, "Write your interpretation or cross-reference…", "Scrivi la tua interpretazione o il tuo collegamento…")}
          required
          value={form.content}
        />
        <div className="flex flex-wrap gap-3">
          <Button className="ritual-button h-auto rounded-full" data-testid="notes-submit-button" disabled={saving} type="submit">
            {editingId
              ? pickText(language, "Update note", "Aggiorna nota")
              : pickText(language, "Save note", "Salva nota")}
          </Button>
          {editingId ? (
            <Button
              className="ghost-button h-auto rounded-full bg-transparent"
              data-testid="notes-cancel-button"
              onClick={cancelEditing}
              type="button"
              variant="ghost"
            >
              {pickText(language, "Cancel", "Annulla")}
            </Button>
          ) : null}
        </div>
      </form>

      <div className="space-y-3" data-testid="notes-list">
        {loading ? (
          <div className="rounded-3xl border border-white/10 bg-black/20 px-4 py-4 text-sm text-slate-300" data-testid="notes-loading-state">
            {pickText(language, "Loading notes…", "Caricamento note…")}
          </div>
        ) : null}

        {!loading && notes.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-black/20 px-4 py-5 text-sm text-slate-400" data-testid="notes-empty-state">
            {pickText(language, "No notes yet for this correspondence.", "Nessuna nota per questa corrispondenza.")}
          </div>
        ) : null}

        {notes.map((note) => (
          <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid={`note-card-${note.id}`} key={note.id}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <p className="text-sm uppercase tracking-[0.24em] text-primary/80" data-testid={`note-card-author-${note.id}`}>
                  {note.author_name || pickText(language, "Anonymous Archivist", "Archivista Anonimo")}
                </p>
                <h5 className="text-2xl text-foreground" data-testid={`note-card-title-${note.id}`}>
                  {note.title}
                </h5>
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid={`note-card-date-${note.id}`}>
                {new Date(note.updated_at).toLocaleString(locale, { dateStyle: "medium" })}
              </p>
            </div>
            <p className="mt-3 text-sm leading-7 text-slate-300" data-testid={`note-card-content-${note.id}`}>
              {note.content}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                className="ghost-button rounded-full bg-transparent px-4 py-2"
                data-testid={`note-edit-button-${note.id}`}
                onClick={() => startEditing(note)}
                type="button"
              >
                <FilePenLine className="h-4 w-4" />
                {pickText(language, "Edit", "Modifica")}
              </button>
              <button
                className="ghost-button rounded-full bg-transparent px-4 py-2 text-red-200 hover:border-red-400/30 hover:text-red-200"
                data-testid={`note-delete-button-${note.id}`}
                onClick={() => handleDelete(note.id)}
                type="button"
              >
                <Trash2 className="h-4 w-4" />
                {pickText(language, "Delete", "Elimina")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};